import { AxiosError } from 'axios';
import { AxiosService } from '../../axios/axios.service';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';
import { BASE_URL_V2 } from '../../axios/axios.constants';
import { tIntercomJWTResponse, tIntercomJWTError } from './IntercomJWT.types';

/**
 * Decodes JWT and extracts expiration timestamp
 * @param jwt - The JWT token to decode
 * @returns Expiration timestamp in seconds, or null if decoding fails
 */
export function decodeJWTExp(jwt: string): number | null {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    );

    if (typeof decoded.exp === 'number') {
      return decoded.exp;
    }

    return null;
  } catch (error) {
    LLog.error(
      {
        component: LOG_COMPONENTS.HTTP,
        method: 'decodeJWTExp',
        operation: LOG_OPERATIONS.ERROR,
      },
      'Failed to decode JWT exp',
      error instanceof Error ? error : new Error(String(error)),
    );
    return null;
  }
}

/**
 * Gets Intercom JWT from backend BFF
 * @param userId - Firebase UID
 * @param idToken - Firebase ID token (fresh, obtained with getIdToken(true))
 * @returns Promise resolving to Intercom JWT string
 * @throws tIntercomJWTError with appropriate error code
 */
export async function getIntercomJWT(
  userId: string,
  idToken: string,
): Promise<string> {
  try {
    LLog.info(
      {
        component: LOG_COMPONENTS.HTTP,
        method: 'getIntercomJWT',
        operation: LOG_OPERATIONS.API_CALL,
      },
      `Fetching Intercom JWT for user ${userId}`,
    );

    // Get API key from localStorage
    const defaultHeaders = localStorage.getItem('apiKey')
      ? { 'X-Api-Key': localStorage.getItem('apiKey') || '' }
      : {};

    // Get Intercom JWT from backend BFF
    const response = await AxiosService.get<tIntercomJWTResponse>(
      `/intercom/jwt/${userId}`,
      {
        baseURL: BASE_URL_V2,
        headers: {
          ...defaultHeaders,
          Authorization: `Bearer ${idToken}`,
        },
      },
    );

    if (!response?.data?.intercom_user_jwt) {
      throw new Error(
        'Invalid response format: missing intercom_user_jwt in response.data',
      );
    }

    LLog.info(
      {
        component: LOG_COMPONENTS.HTTP,
        method: 'getIntercomJWT',
        operation: LOG_OPERATIONS.RESPONSE,
      },
      'Successfully fetched Intercom JWT',
    );

    return response.data.intercom_user_jwt;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;

    if (axiosError.response) {
      const status = axiosError.response.status;
      const errorMessage =
        axiosError.response.data?.message || axiosError.message;

      if (status === 401) {
        LLog.error(
          {
            component: LOG_COMPONENTS.HTTP,
            method: 'getIntercomJWT',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Unauthorized: ID token expired or invalid',
          axiosError,
        );
        throw {
          code: 'UNAUTHORIZED' as const,
          message: 'ID token expired or invalid. Please refresh and retry.',
          originalError: error,
        } as tIntercomJWTError;
      }

      if (status === 403) {
        LLog.error(
          {
            component: LOG_COMPONENTS.HTTP,
            method: 'getIntercomJWT',
            operation: LOG_OPERATIONS.ERROR,
          },
          `Forbidden: userId mismatch. Requested ${userId}`,
          axiosError,
        );
        throw {
          code: 'FORBIDDEN' as const,
          message: `Access denied. Verify that userId matches Firebase UID.`,
          originalError: error,
        } as tIntercomJWTError;
      }

      LLog.error(
        {
          component: LOG_COMPONENTS.HTTP,
          method: 'getIntercomJWT',
          operation: LOG_OPERATIONS.ERROR,
        },
        `HTTP error ${status}: ${errorMessage}`,
        axiosError,
      );
      throw {
        code: 'UNKNOWN' as const,
        message: errorMessage || `HTTP ${status} error`,
        originalError: error,
      } as tIntercomJWTError;
    }

    // Network error or other non-HTTP errors
    LLog.error(
      {
        component: LOG_COMPONENTS.HTTP,
        method: 'getIntercomJWT',
        operation: LOG_OPERATIONS.ERROR,
      },
      'Network error or request failed',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw {
      code: 'NETWORK_ERROR' as const,
      message: 'Network error. Please check your connection and try again.',
      originalError: error,
    } as tIntercomJWTError;
  }
}
